import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { assetUrl } from "@/lib/api";
import { SERVICES } from "@/data/services";
import { Eyebrow, FadeUp, MaskedLines } from "@/components/Reveal";

export default function Services() {
  const { settings } = useSettings();

  return (
    <div data-testid="services-page">
      <section className="relative overflow-hidden" data-testid="services-hero">
        <div aria-hidden className="pointer-events-none absolute -right-40 -top-40 h-[500px] w-[500px] rounded-full border border-cyan-400/10 animate-spin-slow" />
        <div className="mx-auto max-w-7xl px-4 pb-12 pt-14 sm:px-6 lg:px-12 lg:pb-16 lg:pt-20">
          <Eyebrow>Services</Eyebrow>
          <h1 className="mt-6 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="services-title">
            <MaskedLines baseDelay={0.2} lines={[<>Everything your project</>, <span className="bg-gradient-to-r from-cyan-300 to-indigo-400 bg-clip-text text-transparent">needs. Under one planet.</span>]} />
          </h1>
          <p className="mt-6 max-w-xl text-base text-slate-400 sm:text-lg" data-testid="services-subline">
            Our mission is your success — highly customizable, all-in-one solutions with easy scalability.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          const img = assetUrl(settings?.images?.[`service-${s.key}`] || settings?.images?.[s.imageKey]);
          return (
            <FadeUp key={s.key} className="border-t border-white/5 py-14 lg:py-20" data-testid={`service-section-${s.key}`}>
              <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
                <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-sm text-cyan-400">{String(i + 1).padStart(2, "0")}</span>
                    <span className="h-px w-12 bg-cyan-400/40" />
                    <Icon className="h-5 w-5 text-slate-400" />
                  </div>
                  <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-white lg:text-4xl" data-testid={`service-title-${s.key}`}>{s.title}</h2>
                  <p className="mt-4 text-base leading-relaxed text-slate-400" data-testid={`service-blurb-${s.key}`}>{s.blurbLong}</p>
                  <ul className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300" data-testid={`service-feature-${s.key}`}>
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    data-testid={`service-cta-${s.key}`}
                    className="group mt-8 inline-flex items-center gap-2 rounded-full border border-cyan-400/40 px-6 py-3 text-sm font-semibold text-cyan-300 transition hover:bg-cyan-400/10"
                  >
                    Build my package
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
                <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                  <div className="group overflow-hidden rounded-2xl border border-white/10">
                    <img
                      src={img}
                      alt={s.title}
                      className="h-72 w-full object-cover transition duration-700 group-hover:scale-105 lg:h-[420px]"
                      data-testid={`service-image-${s.key}`}
                    />
                  </div>
                </div>
              </div>
            </FadeUp>
          );
        })}
      </div>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-12 lg:pb-28" data-testid="services-cta">
        <FadeUp className="rounded-3xl border border-cyan-400/20 bg-gradient-to-br from-ink2 to-[#0d1a2b] p-10 text-center lg:p-16">
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">You build the package. We make it happen.</h2>
          <p className="mx-auto mt-4 max-w-lg text-slate-400">Mix and match any disciplines — online or local, one team delivers it all.</p>
          <Link to="/contact" data-testid="services-cta-button" className="mt-8 inline-flex items-center gap-2 rounded-full bg-cyan-400 px-8 py-4 text-sm font-semibold text-ink transition hover:bg-cyan-300">
            Get a proposal <ArrowRight className="h-4 w-4" />
          </Link>
        </FadeUp>
      </section>
    </div>
  );
}
